import {
  ApolloClient,
  createHttpLink,
  DefaultOptions,
  from,
  InMemoryCache,
  NormalizedCacheObject,
  ApolloLink,
  ServerError,
  HttpLink,
} from "@apollo/client";
import { CustomRestLink } from "./CustomRestLink";
import { ApolloProvider } from "@apollo/client/react";
import { setContext } from "@apollo/client/link/context";
import { useIsAuthenticated } from "@azure/msal-react";
import { InteractionRequiredAuthError, BrowserAuthError, SilentRequest } from "@azure/msal-browser";
import { useAccount, useMsal } from "@azure/msal-react";
import { onError, ErrorLink } from "@apollo/client/link/error";
import { useNavigate } from "react-router-dom";
import SessionManager from "utils/sessionManager";
import { tenantSettings } from "utils/authentication/tenantSettings";
import App from "App";
import { getScormAccessTokenFromSessionStorage } from "utils/authentication/helpers";
import { useState, useMemo } from "react";
import { BOOKING_JOURNEY, SESSION_STORAGE_KEYS } from "utils/constants";
import { GetApolloLinkWithToken } from "./AuthenticationHelpers";
import { CombinedGraphQLErrors, CombinedProtocolErrors } from "@apollo/client/errors";
import { Loader } from "components/loader/loader";
import { ErrorBoundary } from "components/error-boundary/error-boundary";

export const AzureApolloConnection = (): React.ReactElement => {
  // ALL HOOKS MUST BE CALLED FIRST - before any conditional returns
  const { instance, accounts, inProgress } = useMsal();
  const [isLoggedOut, setIsLoggedOut] = useState<boolean>(false);
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || {});
  const navigate = useNavigate();

  const AsyncTokenLookup = async (scopes: string[], forceRefresh = false): Promise<any> => {
    const loginRequest = {
      scopes: scopes,
    } as SilentRequest;
    if (forceRefresh) {
      loginRequest.forceRefresh = true as boolean;
    }

    // If authentication is in progress, return null and let Apollo retry
    if (inProgress !== "none") {
      console.log("Authentication in progress, skipping token acquisition");
      return null;
    }

    // Check if we have an account
    if (!account) {
      console.warn("No account available for token acquisition");
      // Only trigger redirect if we're not authenticated at all
      if (!isAuthenticated) {
        console.log("Not authenticated, triggering redirect");
        return instance.acquireTokenRedirect(loginRequest);
      }
      return null;
    }

    // We have an account and auth is ready, acquire token
    try {
      const result = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      if (!result.fromCache) {
        SessionManager.updateTokenLastFetchedAt();
      }
      console.log("Token acquired successfully");
      return result;
    } catch (err) {
      // Handle interaction required (expired refresh token, consent needed, etc.)
      if (err instanceof InteractionRequiredAuthError) {
        console.log("Interaction required, triggering redirect");
        return instance.acquireTokenRedirect(loginRequest);
      }
      // Handle BrowserAuthError - includes block_iframe_reload when page reloads after long idle
      // This happens when tokens expire and MSAL's hidden iframe token refresh fails
      if (err instanceof BrowserAuthError) {
        console.log("BrowserAuthError (likely stale session), triggering redirect:", err.errorCode);
        return instance.acquireTokenRedirect(loginRequest);
      }
      console.error("Token acquisition error:", err);
      return null;
    }
  };

  const withToken = setContext(async (request, { headers }) => {
    const authenticationResult = await AsyncTokenLookup(tenantSettings.customConfig.api.scopes);
    return GetApolloLinkWithToken(authenticationResult, request, headers);
  });

  const restLink = CustomRestLink({
    uri: process.env.REACT_APP_IBF_URL || "",
  });

  const httpLink = new HttpLink({
    uri: tenantSettings.customConfig.api.baseUrl,
  });

  const errorLink = new ErrorLink(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations, path }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`),
      );
    } else if (CombinedProtocolErrors.is(error)) {
      error.errors.forEach(({ message, extensions }) =>
        console.log(
          `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`,
        ),
      );
    } else {
      // Only navigate to unauthorized if we're authenticated but getting 403/UNAUTHENTICATED
      // Don't redirect during initial authentication process
      if (
        isAuthenticated &&
        account &&
        ((error as ServerError)?.statusCode === 403 || error?.message?.includes("UNAUTHENTICATED"))
      ) {
        navigate("/unauthorized");
        console.warn(`server returned 403`);
      } else {
        console.log(`[Network error]: ${error}`);
      }
    }
  });

  const defaultOptions: DefaultOptions = {
    watchQuery: {
      fetchPolicy: "no-cache",
    },
    query: {
      fetchPolicy: "no-cache",
    },
  };

  // Custom link to append operation name to URI for GraphQL operations
  const appendOperationNameLink = new ApolloLink((operation, forward) => {
    operation.setContext({
      uri: `${tenantSettings.customConfig.api.baseUrl}?${operation.operationName}`,
    });
    return forward ? forward(operation) : null;
  });

  // Memoize Apollo client to prevent recreation on every render
  // Create client once MSAL has completed its initial auth check
  const client: any = useMemo(
    () => {
      // Wait until MSAL has finished its initial auth check
      if (inProgress !== "none") {
        console.log("Delaying Apollo client creation - MSAL auth in progress:", inProgress);
        return null;
      }

      // If authenticated, we must have an account before creating client
      // This ensures AsyncTokenLookup can get tokens when queries execute
      if (isAuthenticated && !account) {
        console.log("Delaying Apollo client creation - authenticated but account not loaded yet");
        return null;
      }

      console.log(
        "Creating Apollo Client - MSAL ready, isAuthenticated:",
        isAuthenticated,
        "hasAccount:",
        !!account,
      );
      return new ApolloClient({
        cache: new InMemoryCache({}),
        link: from([
          errorLink as unknown as ApolloLink,
          withToken,
          restLink,
          appendOperationNameLink,
          httpLink,
        ]),
        defaultOptions: defaultOptions,
      });
    },
    [inProgress, isAuthenticated, account], // Recreate when auth state changes
  );

  const syncTokenForScormPlayer = () => {
    const iframe = document.querySelector("iframe");
    if (iframe) {
      const token = getScormAccessTokenFromSessionStorage();
      const tokenMessage = JSON.stringify({ data: { token: token } });
      const scormHost: string = tenantSettings.customConfig.scormHost;
      iframe.contentWindow.postMessage(tokenMessage, scormHost);
    }
  };

  const onSilentRenew = async () => {
    await AsyncTokenLookup(tenantSettings.customConfig.api.scopes, true);
    if (!tenantSettings.customConfig.useGatewayScopesAsScormScopes) {
      await AsyncTokenLookup(tenantSettings.customConfig.scormApiScopes, true);
    }
    syncTokenForScormPlayer();
  };

  const onLogout = async () => {
    setIsLoggedOut(true);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.FEATURES);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.APPLICATION_STATE);
    await tenantSettings.authService.signOut();
  };

  // Don't render until client is ready
  if (!client) {
    return <Loader isDisplay={true} type="page" />;
  }

  return (
    <ApolloProvider client={client}>
      {!isLoggedOut && (
        <ErrorBoundary>
          <App
            onSilentRenew={onSilentRenew}
            isAuthenticated={isAuthenticated}
            onLogout={onLogout}
          />
        </ErrorBoundary>
      )}
    </ApolloProvider>
  );
};
